import { useState, FormEvent, MouseEvent, ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import { getApiUrl } from "../utils";
import { 
  Shield, 
  FileText, 
  AlertTriangle, 
  Mail, 
  Lock, 
  Bot, 
  Globe, 
  CheckCircle2, 
  ArrowRight, 
  Clock, 
  MapPin, 
  X, 
  ChevronRight, 
  ChevronDown,
  Scale, 
  Eye, 
  FileCheck, 
  Building,
  Sparkles,
  HelpCircle,
  Smartphone
} from "lucide-react";

interface LegalCard {
  id: string;
  icon: ReactNode;
  badge: string;
  title: string;
  description: string;
  buttonText: string;
  fullTitle: string;
  lastUpdated: string;
  content: string[];
}

export default function LegalHub() {
  const [selectedPolicy, setSelectedPolicy] = useState<LegalCard | null>(null);
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "", category: "Agronomy Support" });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [activePreview, setActivePreview] = useState<string>("privacy");

  const legalCards: LegalCard[] = [
    {
      id: "privacy",
      icon: <Shield className="w-6 h-6 text-green-600" />,
      badge: "🛡 Privacy Policy",
      title: "Privacy Policy",
      description: "Learn how we safely process, store, and safeguard your garden photos, botanical log data, and personal details.",
      buttonText: "Read Policy",
      fullTitle: "Plant Identifer - Global Privacy Policy",
      lastUpdated: "June 2026",
      content: [
        "Your privacy is our primary botanical priority. At Plant Identifer, we do not monetize or distribute your custom plant and garden photographs to third-party advertiser channels. Images uploaded via our web or app scanners are processed temporarily on secure, state-of-the-art sandboxed Cloud endpoints to yield taxonomic botanical species indexes and green health scores.",
        "We retain basic user analytics (such as count of plant identifications) solely to scale our database structures dynamically. Any user who creates a registered account retains 100% control over their digital garden catalog. You can permanently wipe your entire history from our database within the settings interface instantly.",
        "Our telemetry endpoints are compliant with strict standard privacy regulations globally. We adhere completely to standard user choice constraints like Do-Not-Track headers. If you have any inquiries regarding data protection policies, write to us directly at contact@plantidentifer.com."
      ]
    },
    {
      id: "terms",
      icon: <FileText className="w-6 h-6 text-blue-600" />,
      badge: "📄 Terms Of Service",
      title: "Terms Of Service",
      description: "Understand the terms, guidelines, user responsibilities, and acceptable community usage rules of Plant Identifier.",
      buttonText: "Read Terms",
      fullTitle: "Plant Identifier - Universal Terms Of Service",
      lastUpdated: "June 2026",
      content: [
        "Welcome to Plant Identifier. By utilizing our computer vision scanners, mobile companion packages, or community databases, you agree to comply with our botanical guidelines.",
        "Our identification tool is designed for educational study, amateur home gardening, crop stress screening, and ecological exploration. Users are forbidden from using our systems to automate malicious botanical research pipelines or stress-testing our public servers with programmatic DDOS queries.",
        "We reserve the right to limit api access tiers, suspend malicious users who violate structural garden community protections, or modify free tier quotas when required to optimize server infrastructure stability."
      ]
    },
    {
      id: "disclaimer",
      icon: <AlertTriangle className="w-6 h-6 text-amber-600" />,
      badge: "⚠ Disclaimer",
      title: "Disclaimer & Limits",
      description: "Important guidance regarding AI-generated plant identifications, toxicity indicators, and care metrics.",
      buttonText: "View Disclaimer",
      fullTitle: "Plant Identifier - Botanical & AI Disclaimer",
      lastUpdated: "June 2026",
      content: [
        "All species identifications, disease diagnoses, toxicity guides, and care advice generated by the Plant Identifier neural systems are strictly for educational and informational entertainment purposes.",
        "While our deep-learning model achieves a high 95%+ accuracy index on core decorative flora, AI predictions do not replace licensed agrarian diagnostics, professional soil test parameters, or state health inspection services.",
        "Never consume unknown plants, berries, mushrooms, or herbs based solely on our software matches. Plant Identifier does not assume physical liabilities for botanical misidentification or improper agrarian remedy administration."
      ]
    },
    {
      id: "contact",
      icon: <Mail className="w-6 h-6 text-emerald-600" />,
      badge: "📬 Contact Us",
      title: "Contact & Support",
      description: "Get in touch with our certified horticulturalists, vision engineering researchers, or administrative team.",
      buttonText: "Contact Team",
      fullTitle: "Contact Plant Identifier Team",
      lastUpdated: "June 2026",
      content: [
        "Need technical support regarding your custom accounts, or botanic classification inquiries? Our direct help desk is available globally.",
        "For partnerships, educational API integrations, or sponsorship proposals, please request correspondence via contact@plantidentifer.com. We prioritize educational networks and community garden networks.",
        "Our standard office response times average within 24 hours. Fill out the contact inquiry form located below, or reach out directly via our verified email channels."
      ]
    }
  ];

  const policyPreviews = {
    privacy: {
      title: "Privacy Highlights",
      bullets: [
        "90-Day Automatic Log Expiry inside basic unlinked scan cache lines.",
        "Zero tracking cookies or cross-site advertiser telemetry.",
        "CCPA & EU GDPR standard rights for absolute user-controlled erasure.",
        "Media attachments are strictly sandboxed and stored anonymously."
      ]
    },
    terms: {
      title: "Terms Guidelines",
      bullets: [
        "Free global lifetime personal use allowed across all standard devices.",
        "No automated scraping or bulk farming of our taxonomy indices.",
        "Proper brand attribution required for school or university reports.",
        "Subscription tiers, if introduced, will remain optional for cloud saves."
      ]
    },
    disclaimer: {
      title: "Accuracy & Limits",
      bullets: [
        "Not a certified tool for commercial toxic crop classification.",
        "Environmental stress features require rich sunlight during capture.",
        "Always cross-reference with professional botanical resources.",
        "No absolute guarantees of perfect taxonomic species certainty."
      ]
    },
    cookies: {
      title: "Cookie Information",
      bullets: [
        "We utilize standard functional state tokens only (e.g., login tokens).",
        "Absolutely no commercial behavioral retargeting cookies used.",
        "We support standard opt-out vectors across legal environments.",
        "User choices are cached locally to minimize cookie overhead."
      ]
    }
  };

  const handleContactSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!contactForm.email || !contactForm.message) return;
    setIsSubmitting(true);
    setSubmitError("");
    try {
      // 1. Submit directly to Formspree from user browser for robust deliverability
      const formId = "xgojoozo";
      const formspreeResponse = await fetch(`https://formspree.io/f/${formId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          name: contactForm.name,
          email: contactForm.email,
          category: contactForm.category,
          message: contactForm.message
        })
      });

      if (!formspreeResponse.ok) {
        const errData = await formspreeResponse.json().catch(() => ({}));
        const formspreeError = errData.errors && Array.isArray(errData.errors)
          ? errData.errors.map((e: any) => e.message).join(", ")
          : errData.error || "Formspree submission failed.";
        throw new Error(formspreeError || "Failed to deliver your message. Please try again.");
      }

      // 2. Silently proxy back to database logging
      try {
        await fetch(getApiUrl("/api/contact"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            name: contactForm.name,
            email: contactForm.email,
            category: contactForm.category,
            message: contactForm.message,
            skipFormspree: true // Bypass secondary server dispatch
          })
        });
      } catch (proxyErr) {
        console.warn("Backend proxy contact submission skipped:", proxyErr);
      }

      setIsSubmitted(true);
      setContactForm({ name: "", email: "", message: "", category: "Agronomy Support" });
      setIsDropdownOpen(false);
      setTimeout(() => {
        setIsSubmitted(false);
      }, 8000);
    } catch (err: any) {
      setSubmitError(err?.message || "Network error. Please try again with an active connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="legal" 
      className="pt-6 pb-6 bg-white relative overflow-hidden"
      aria-labelledby="legal-heading"
    >
      
      {/* Premium organic environmental back-light glow spots */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-green-500/[0.015] rounded-full blur-[140px] pointer-events-none -z-10" />
      <div className="absolute bottom-1/3 right-[5%] w-96 h-96 bg-emerald-400/[0.02] rounded-full blur-[120px] pointer-events-none -z-10 animate-pulse" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">

        {/* ==================== SECTION HEADER ==================== */}
        <div className="text-center max-w-3xl mx-auto mb-10 space-y-4">
          
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-green-50/90 border border-green-150/40 rounded-full text-green-700 text-xs font-extrabold uppercase tracking-widest shadow-2xs"
          >
            <span>🛡</span> Trust & Transparency
          </motion.div>

          {/* Heading */}
          <motion.h2
            id="legal-heading"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="text-4xl sm:text-5xl font-[900] text-gray-950 tracking-tight leading-[1.12]"
          >
            Your Privacy And Trust <br />
            Matter To Us
          </motion.h2>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="text-lg text-gray-500 font-medium max-w-[800px] mx-auto leading-relaxed"
          >
            We believe in transparency, responsible AI alignment, and protecting user data while delivering the world's most accessible plant companion system.
          </motion.p>
        </div>


        {/* ==================== GRID ROW: 4 PREMIUM LEGAL CARDS ==================== */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
          {legalCards.map((card, idx) => (
            <motion.div
              layout
              key={card.id}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-[26px] border border-gray-150 p-7.5 shadow-[0_8px_30px_rgba(0,0,0,0.012)] hover:shadow-[0_20px_45px_rgba(34,197,94,0.025)] hover:border-green-200 transition-all duration-300 flex flex-col justify-between relative group"
            >
              {/* Card Green glow border */}
              <div className="absolute inset-0 rounded-[26px] border border-transparent group-hover:border-green-200/50 pointer-events-none transition-colors" />

              <div className="space-y-6">
                
                {/* Icon wrapper circular style */}
                <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform duration-350">
                  {card.icon}
                </div>

                <div className="space-y-2.5 text-left">
                  <span className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider block">
                    {card.badge.split(" ")[0]} COMPLIANCE
                  </span>
                  
                  <h3 className="text-lg font-black text-gray-950 tracking-tight leading-none group-hover:text-green-700 transition-colors">
                    {card.title}
                  </h3>

                  <p className="text-xs text-gray-500 leading-relaxed font-semibold">
                    {card.description}
                  </p>
                </div>

              </div>

              {/* Action read button */}
              <div className="pt-6 mt-6 border-t border-gray-100">
                <button
                  onClick={() => setSelectedPolicy(card)}
                  className="w-full py-3 bg-gray-50 hover:bg-green-50 text-gray-700 hover:text-green-700 border border-gray-100 hover:border-green-150 rounded-xl text-xs font-black transition-all cursor-pointer flex items-center justify-center gap-1.5"
                >
                  <span>{card.buttonText}</span>
                  <ChevronRight className="w-4 h-4 shrink-0" />
                </button>
              </div>

            </motion.div>
          ))}
        </div>


        {/* ==================== DUAL PANEL ROW: CONTACT & CHECKLIST ==================== */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-10 items-stretch">
          
          {/* CONTACT INFO CARD PANEL (LEFT 7 COLS) */}
          <div className="lg:col-span-7" id="contact">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-[32px] border border-gray-155 p-8 sm:p-10 relative overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.01)] flex flex-col justify-between h-full"
            >
              
              <div className="space-y-6">
                
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📬</span>
                  <div>
                    <h4 className="text-xl sm:text-2xl font-[900] text-gray-950 tracking-tight">
                      Support & Help Desk
                    </h4>
                    <p className="text-xs text-gray-450 font-bold">Have direct botanical inquiries or business proposals?</p>
                  </div>
                </div>

                {/* Form area */}
                <form onSubmit={handleContactSubmit} className="space-y-4 pt-4 relative z-10">
                  {isSubmitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="p-6 bg-green-500/10 border border-green-500/20 rounded-2xl flex items-center gap-4 text-green-800"
                    >
                      <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />
                      <div>
                        <span className="text-sm font-black block">Inquiry Submitted Successfully!</span>
                        <p className="text-xs text-green-700/80 font-semibold mt-0.5">We have received your botanical inquiry and our helpdesk will respond back via email within 24 hours.</p>
                      </div>
                    </motion.div>
                  ) : (
                    <div className="space-y-4">
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider block">Your Name</label>
                          <input 
                            type="text" 
                            required
                            disabled={isSubmitting}
                            placeholder="Sarah Mitchell" 
                            value={contactForm.name}
                            onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                            className="w-full bg-gray-55/35 border border-gray-150 rounded-xl px-4 py-3 text-xs font-bold text-gray-900 outline-hidden focus:border-green-300 focus:bg-white transition-all shadow-3xs disabled:opacity-60"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider block">Your Email</label>
                          <input 
                            type="email" 
                            required
                            disabled={isSubmitting}
                            placeholder="sarah@example.com" 
                            value={contactForm.email}
                            onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                            className="w-full bg-gray-55/35 border border-gray-150 rounded-xl px-4 py-3 text-xs font-bold text-gray-900 outline-hidden focus:border-green-300 focus:bg-white transition-all shadow-3xs disabled:opacity-60"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1 relative" id="inquiry-class-dropdown-container">
                          <label className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider block">Inquiry Class</label>
                          
                          {/* Dropdown Trigger */}
                          <button
                            type="button"
                            id="inquiry-class-dropdown-trigger"
                            disabled={isSubmitting}
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className={`w-full bg-gray-55/35 border text-left px-4 py-3 text-xs font-bold text-gray-900 rounded-xl flex items-center justify-between outline-hidden transition-all shadow-3xs hover:bg-white disabled:opacity-60 cursor-pointer ${
                              isDropdownOpen ? "border-green-400 bg-white ring-2 ring-green-100/50" : "border-gray-150"
                            }`}
                          >
                            <span>{contactForm.category}</span>
                            <motion.div
                              animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                              transition={{ duration: 0.2, ease: "easeInOut" }}
                              className="text-gray-400"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </motion.div>
                          </button>

                          {/* Options Overlay List */}
                          <AnimatePresence>
                            {isDropdownOpen && (
                              <>
                                {/* Click Outside Backdrop */}
                                <div 
                                  className="fixed inset-0 z-30 cursor-default" 
                                  onClick={() => setIsDropdownOpen(false)}
                                />
                                
                                <motion.div
                                  initial={{ opacity: 0, y: -6, scale: 0.98 }}
                                  animate={{ opacity: 1, y: 0, scale: 1 }}
                                  exit={{ opacity: 0, y: -6, scale: 0.98 }}
                                  transition={{ duration: 0.15, ease: "easeOut" }}
                                  className="absolute top-full left-0 right-0 mt-1.5 z-40 bg-white border border-gray-150 shadow-md rounded-xl overflow-hidden py-1 divide-y divide-gray-50 max-h-[220px] overflow-y-auto"
                                >
                                  {[
                                    "Agronomy Support",
                                    "API & Partnership",
                                    "Privacy & Erasure",
                                    "General Support"
                                  ].map((category) => {
                                    const isSelected = contactForm.category === category;
                                    return (
                                      <button
                                        key={category}
                                        type="button"
                                        onClick={() => {
                                          setContactForm({ ...contactForm, category });
                                          setIsDropdownOpen(false);
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-xs font-semibold transition-all flex items-center justify-between cursor-pointer ${
                                          isSelected 
                                            ? "bg-green-50 text-green-700" 
                                            : "text-gray-700 hover:bg-gray-50 hover:text-green-600"
                                        }`}
                                      >
                                        <span>{category}</span>
                                        {isSelected && (
                                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                                        )}
                                      </button>
                                    );
                                  })}
                                </motion.div>
                              </>
                            )}
                          </AnimatePresence>
                        </div>

                        <div className="space-y-1">
                          <label className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider block">Response Profile</label>
                          <div className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-xs font-black text-gray-650 flex items-center gap-1.5 select-none text-emerald-700">
                            <Clock className="w-3.5 h-3.5 shrink-0" />
                            <span>Averages within 24 Hours</span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider block">Inquiry Message</label>
                        <textarea 
                          rows={3}
                          required
                          disabled={isSubmitting}
                          placeholder="How can our community assist you today..." 
                          value={contactForm.message}
                          onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                          className="w-full bg-gray-55/35 border border-gray-150 rounded-xl px-4 py-3 text-xs font-bold text-gray-900 outline-hidden focus:border-green-300 focus:bg-white transition-all shadow-3xs disabled:opacity-60"
                        />
                      </div>

                      {submitError && (
                        <div className="p-4 bg-red-55/40 border border-red-200 text-red-800 rounded-xl text-xs font-semibold leading-relaxed">
                          ⚠️ {submitError}
                        </div>
                      )}

                      <div className="pt-2 text-right">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white font-extrabold text-xs tracking-wider uppercase rounded-xl transition-all shadow-md shadow-green-150/45 cursor-pointer inline-flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" id="legal-contact-spinner">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Sending...
                            </>
                          ) : (
                            <>
                              Send Inquiry <ArrowRight className="w-4 h-4 shrink-0" />
                            </>
                          )}
                        </button>
                      </div>

                    </div>
                  )}
                </form>

              </div>

              {/* Raw mail links footer */}
              <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-6 mt-8">
                <div className="text-left">
                  <span className="text-[9px] text-gray-400 font-extrabold uppercase block tracking-wider">SUPPORT DESK</span>
                  <a href="mailto:contact@plantidentifer.com" className="text-xs sm:text-sm font-black text-gray-905 hover:text-green-700 transition-colors break-all">contact@plantidentifer.com</a>
                </div>
                <div className="text-left">
                  <span className="text-[9px] text-gray-400 font-extrabold uppercase block tracking-wider">BUSINESS / PARTNERSHIP</span>
                  <a href="mailto:contact@plantidentifer.com" className="text-xs sm:text-sm font-black text-gray-905 hover:text-green-700 transition-colors break-all">contact@plantidentifer.com</a>
                </div>
              </div>

            </motion.div>
          </div>

          {/* LEGAL HIGHLIGHTS PANEL (RIGHT 5 COLS) */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-[32px] p-8 sm:p-10 flex flex-col justify-between h-full relative overflow-hidden shadow-lg"
            >
              {/* Back ambient accent circle */}
              <div className="absolute bottom-[-80px] right-[-85px] w-60 h-60 bg-white/10 rounded-full blur-3xl pointer-events-none" />

              <div className="space-y-6 relative z-10">
                <span className="inline-flex items-center gap-1.5 bg-white/15 border border-white/20 rounded-md px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-emerald-100">
                  ✔ HIGH-INTEGRITY EMBED
                </span>

                <h4 className="text-2xl font-black tracking-tight leading-snug">
                  Compliance Highlights
                </h4>

                <p className="text-xs text-white/90 leading-relaxed font-bold">
                  We maintain strict guidelines matching top-tier digital startups. Our code uses a standard, lightweight stack targeting high-performance page scores.
                </p>
              </div>

              {/* Highlight checklist list */}
              <div className="space-y-4 mt-8 pt-6 border-t border-white/15 relative z-10 text-left">
                {[
                  "GDPR Friendly Structure",
                  "Privacy First Design",
                  "Secure Platform Safeguards",
                  "Transparent Data Practices",
                  "User Controlled Accounts",
                  "Responsible AI Identification"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-xs font-black text-white">
                    <CheckCircle2 className="w-4 h-4 text-emerald-300 fill-white/10 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="pt-6 mt-6 border-t border-white/15 text-[10px] text-emerald-100/80 font-black tracking-wider uppercase flex items-center gap-1.5 select-none">
                <Building className="w-3.5 h-3.5" />
                <span>PLANT IDENTIFER GLOBAL SERVICES 2026</span>
              </div>

            </motion.div>
          </div>

        </div>


        {/* ==================== POLICY PREVIEW TABS AREA ==================== */}
        <div className="space-y-6 text-center">
          <span className="text-[10px] text-gray-400 font-extrabold uppercase tracking-widest block">
            QUICK POLICY MATRIX CHECKS
          </span>

          <div className="bg-gray-50 border border-gray-150 rounded-2xl p-6 sm:p-8 max-w-2xl mx-auto flex flex-col md:flex-row gap-6 items-start text-left">
            
            {/* Left buttons tabs */}
            <div className="flex flex-row md:flex-col gap-1.5 w-full md:w-44 overflow-x-auto shrink-0 pb-2 md:pb-0">
              {[
                { key: "privacy", label: "01. Privacy Over" },
                { key: "terms", label: "02. Terms Over" },
                { key: "disclaimer", label: "03. Limits Over" },
                { key: "cookies", label: "04. Cookies Over" }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActivePreview(tab.key)}
                  className={`px-3.5 py-2 rounded-xl text-[11px] font-black uppercase text-left tracking-wider cursor-pointer whitespace-nowrap transition-all ${
                    activePreview === tab.key
                      ? "bg-white border border-gray-200 text-green-700 shadow-3xs"
                      : "text-gray-400 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Right bullet statements */}
            <div className="flex-1 space-y-4">
              <h5 className="text-xs font-black uppercase text-gray-400 flex items-center gap-1">
                <FileCheck className="w-3.5 h-3.5 text-green-600" />
                <span>{policyPreviews[activePreview as keyof typeof policyPreviews].title}</span>
              </h5>

              <ul className="space-y-2.5">
                {policyPreviews[activePreview as keyof typeof policyPreviews].bullets.map((bullet, bIdx) => (
                  <li key={bIdx} className="flex gap-2.5 items-start text-xs text-gray-650 font-bold leading-relaxed">
                    <span className="text-green-500 font-black shrink-0">✔</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>

      </div>


      {/* ==================== INTERACTIVE DETAILED POLICY READER OVERLAY ==================== */}
      <AnimatePresence>
        {selectedPolicy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-1000"
            role="dialog"
            aria-modal="true"
          >
            <motion.div
              initial={{ scale: 0.95, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 15 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-[32px] overflow-hidden max-w-xl w-full max-h-[80vh] flex flex-col shadow-2xl relative"
            >
              
              {/* Close Button Anchor */}
              <button
                onClick={() => setSelectedPolicy(null)}
                className="absolute top-5 right-5 w-9 h-9 rounded-full bg-black/60 text-white hover:bg-black/85 flex items-center justify-center cursor-pointer transition-colors z-20"
                aria-label="Close policy modal"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Scrollable container for text display */}
              <div className="overflow-y-auto flex-1 p-8 sm:p-10 space-y-6">
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center shrink-0">
                    {selectedPolicy.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-gray-950 tracking-tight">
                      {selectedPolicy.fullTitle}
                    </h3>
                    <span className="text-[10px] text-[gray-400] font-extrabold uppercase block tracking-wider">
                      Last Updated: {selectedPolicy.lastUpdated}
                    </span>
                  </div>
                </div>

                <div className="space-y-4 text-xs sm:text-sm text-gray-600 leading-relaxed font-semibold">
                  {selectedPolicy.content.map((para, paraIdx) => (
                    <p key={paraIdx}>
                      {para}
                    </p>
                  ))}
                </div>

                <div className="p-4 bg-gray-50 border border-gray-150 rounded-2xl text-[10px] text-gray-400 font-extrabold uppercase leading-relaxed text-center">
                  🛡 Strict Environmental Compliance - Plant Identifier Platform 2026
                </div>

              </div>

              {/* Modal footer action */}
              <div className="p-5 bg-gray-55/30 border-t border-gray-100 flex items-center justify-between">
                <span className="text-[9px] text-gray-400 font-extrabold uppercase">
                  FREE LIFETIME BOTANY ACCESS
                </span>
                
                <button
                  onClick={() => setSelectedPolicy(null)}
                  className="px-4 py-2 bg-green-600 text-white text-xs font-black rounded-lg hover:bg-green-700 transition"
                >
                  I Understand
                </button>
              </div>

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </section>
  );
}
